import classNames from 'classnames';
import { ReactNode, createElement, type ElementType } from 'react';

type Props = {
  as?: ElementType;
  color?: 'default' | 'primary' | 'text' | 'delete';
  size?: 'small';
  rounded?: boolean;
  className?: string;
  outlined?: boolean;
  loading?: boolean;
  children?: ReactNode;
  [x: string]: unknown;
  /** Control data-usetiful attribute in the underlying Button (defaults to empty string there) */
  dataUsetiful?: string;
};

/**
 * Use `Button` to apply button styling to a specified element type.
 *
 * Will accept a component as the element type, useful for router links.
 */
export const Button = ({
  as = 'button',
  color = 'default',
  size,
  rounded = false,
  outlined = false,
  loading = false,
  dataUsetiful = '',
  ...props
}: Props) => {
  const { className, children, ...restProps } = props as {
    className?: string;
    children?: ReactNode;
  };

  const isPrimary = color === 'primary';
  const isText = color === 'text';
  const isDefault = color === 'default';

  const baseClasses = classNames(
    // base layout/typography
    'inline-block py-2 px-4 text-sm font-medium border-2 border-transparent rounded-full text-center no-underline outline-none cursor-pointer whitespace-nowrap overflow-hidden relative hover:scale-102 transition-all duration-200 hover:shadow-md active:scale-95 active:shadow-none ease-spring',
    // font family (from CSS var)
    '[font-family:var(--font-display)]',
    // transform
    rounded ? 'capitalize' : 'uppercase'
  );

  const colorClasses = classNames(
    isDefault &&
      'bg-transparent text-[var(--color-blue-dark)] shadow-none px-2',
    isPrimary &&
      'bg-[var(--color-brand)] text-white active:brightness-100 active:bg-brand-darker hover:brightness-[130%]',
    isText && 'p-0 bg-transparent shadow-none text-brand hover:underline',
    color === 'delete' &&
      'bg-transparent text-[var(--color-delete)] shadow-none'
  );

  const outlinedClasses = classNames(
    outlined &&
      isPrimary &&
      'bg-inherit hover:bg-inherit border border-[var(--color-brand)] text-[var(--color-brand)]'
  );

  const sizeClasses = classNames(
    size === 'small' && 'p-2 text-xs border-0 rounded-lg shadow'
  );

  const loadingClasses = classNames(
    loading && 'text-transparent pointer-events-none'
  );

  const spinnerColorClass = classNames(
    isPrimary && outlined && 'bg-[var(--color-blue-light)]',
    isPrimary && !outlined && 'bg-[var(--color-smoke)]',
    (isDefault || isText || color === 'delete') && 'bg-[var(--color-brand)]'
  );

  const content: ReactNode = loading ? (
    <>
      <span className='opacity-0'>{children}</span>
      <span
        aria-hidden
        className={classNames(
          'absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full',
          spinnerColorClass
        )}
      />
    </>
  ) : (
    children
  );

  return createElement(
    as,
    {
      ...restProps,
      'data-usetiful': dataUsetiful,
      'aria-busy': loading || undefined,
      className: classNames(
        baseClasses,
        colorClasses,
        outlinedClasses,
        sizeClasses,
        loadingClasses,
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        className
      ),
    },
    content
  );
};
